import { ButtonBase } from '@mui/material';
import clsx from 'clsx';
import { useState } from 'react';

import { useParsedDirectiveRows } from './use-parsed-directive-rows.js';

import { DirectiveApplyBar } from './directive-apply-bar.js';
import { PinListInput } from './pin-list-input.js';

export interface PinoutTableMarkdownItemProps {
	/** Raw JSON string containing the pinout array. */
	jsonData?: string;
}

interface PinoutRow {
	signalName: string;
	pins: string[];
}

const editInputClass =
	'w-full bg-transparent border-b border-gray-a6 focus:outline-none focus:border-blue-9 px-1 py-0.5';

function parsePinout(jsonData: string): PinoutRow[] | null {
	try {
		const parsed: unknown = JSON.parse(jsonData);
		if (!Array.isArray(parsed)) {
			return null;
		}
		const rows = parsed.filter((item): item is PinoutRow => {
			if (typeof item !== 'object' || item === null) {
				return false;
			}
			const r = item as Record<string, unknown>;
			return (
				typeof r['signalName'] === 'string' &&
				Array.isArray(r['pins']) &&
				(r['pins'] as unknown[]).every((p) => typeof p === 'string')
			);
		});
		return rows.length > 0 ? rows : null;
	} catch {
		return null;
	}
}

/**
 * Markdown directive component that renders a compact pinout table in a workbench message.
 *
 * Each row shows the signal name and its associated pins as a comma-separated list.
 * Rows are editable inline before applying.
 *
 * @param jsonData - Raw JSON string representing an array of {@link PinoutRow} objects.
 * @returns The rendered table, or null if `jsonData` is missing or cannot be parsed.
 */
export function PinoutTableMarkdownItem({ jsonData }: PinoutTableMarkdownItemProps) {
	const [rows, setRows] = useParsedDirectiveRows(jsonData, parsePinout);
	const [isEditing, setIsEditing] = useState(false);

	if (!rows) {
		return null;
	}

	function updateRow(index: number, patch: Partial<PinoutRow>) {
		setRows((prev) => (prev ? prev.map((r, i) => (i === index ? { ...r, ...patch } : r)) : null));
	}

	return (
		<>
			<div className='my-2 overflow-x-auto rounded border border-gray-a4 text-sm'>
				<table className='w-full border-collapse'>
					<thead>
						<tr className='bg-gray-a2 text-left text-xs font-semibold text-gray-11'>
							<th className='px-3 py-2'>Signal name</th>
							<th className='px-3 py-2'>Pins</th>
							<th className='px-3 py-2 text-right'>
								<ButtonBase
									className='font-normal tracking-normal text-gray-11 normal-case hover:text-gray-12'
									onClick={() => setIsEditing((v) => !v)}
								>
									{isEditing ? 'Done' : 'Edit'}
								</ButtonBase>
							</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => (
							<tr key={index} className={clsx('border-t border-gray-a3', index % 2 === 1 && 'bg-gray-a2')}>
								<td className='px-3 py-1.5 font-mono font-medium'>
									{isEditing ?
										<input
											type='text'
											value={row.signalName}
											onChange={(e) => updateRow(index, { signalName: e.target.value })}
											className={editInputClass}
										/>
									:	row.signalName}
								</td>
								<td className='px-3 py-1.5 text-gray-11'>
									{isEditing ?
										<PinListInput
											pins={row.pins}
											onChange={(pins) => updateRow(index, { pins })}
											className={editInputClass}
										/>
									:	row.pins.join(', ')}
								</td>
								<td />
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<DirectiveApplyBar data={rows} configId='pinouts' />
		</>
	);
}
